import { format } from "date-fns";
import { parseServerDate, parseServerDateOnly, toServerDateOnly, toServerEndOfDay } from "@persolog/utils";
import {
    Banner,
    BannerApp,
    BannerImageVariant,
    BannerState,
    StartPageBanner,
} from "../types/BannerTypes.ts";

/** Every frontend a banner can target, in the order the editor lists them. */
export const BANNER_APPS: BannerApp[] = ["PROFILE_ADMINISTRATION", "QUESTIONNAIRE", "PROFILE_DOWNLOAD"];

/** The language every banner must carry, because the frontends fall back to it. */
export const REQUIRED_LANGUAGE = "en";

/**
 * The interface languages the customer-facing frontends offer — the set a banner can be authored
 * in. Deliberately not this app's own `availableLocales`: the admin ships de/en only, while a
 * trainer or participant can pick any of these (mirrors packages/i18n/src/locales).
 */
export const BANNER_LANGUAGES = [
    "da", "de", "en", "es", "fr", "hr", "hu", "it", "nl", "no", "pl", "pt", "ro", "sl",
];

/**
 * What the list shows at a glance. "inactive" wins over the window: a banner nobody switched on is
 * off regardless of its dates, and saying "expired" about it would suggest it had ever been up.
 *
 * Takes only the three fields it reads, so the notification banners and the start-page ones share it
 * — they deliberately share the active flag and the display window (RDT-359).
 */
export function bannerState(
    banner: Pick<Banner, "active" | "startsAt" | "endsAt">,
    now: Date = new Date(),
): BannerState {
    if (!banner.active) return "inactive";
    if (banner.startsAt && now < parseServerDate(banner.startsAt)) return "scheduled";
    if (banner.endsAt && now > parseServerDate(banner.endsAt)) return "expired";
    return "active";
}

/** A blank banner for the create dialog: off, open-ended, everyone, no frontend picked yet. */
export function emptyBanner(): Banner {
    return {
        id: null,
        active: false,
        startsAt: null,
        endsAt: null,
        audience: "ALL",
        apps: [],
        texts: [],
    };
}

/**
 * The same three rules language-management enforces, checked here so the editor can say what is
 * wrong before saving rather than surfacing a 422. Returns an i18n key, or null when the banner is
 * fine.
 */
export function bannerProblem(banner: Banner): string | null {
    if (banner.apps.length === 0) return "banners.error.noApp";
    if (!textFor(banner, REQUIRED_LANGUAGE)) return "banners.error.noEnglish";
    if (banner.startsAt && banner.endsAt && parseServerDate(banner.endsAt) < parseServerDate(banner.startsAt)) {
        return "banners.error.endBeforeStart";
    }
    return null;
}

/** The banner's text in one language, or "" when it has none — blank texts do not count. */
export function textFor(banner: Banner, language: string): string {
    return banner.texts.find((text) => text.language === language && text.text.trim() !== "")?.text ?? "";
}

/**
 * Sets one language's text, dropping the entry entirely when it is blanked, so an emptied tab is not
 * stored as an empty string the frontends would then have to skip.
 *
 * Mutates rather than returning a new banner ON PURPOSE. The editor calls this on every keystroke,
 * and replacing the object (or even just the texts array) re-rendered the whole dialog each time —
 * fast typing then lost characters outright: typing 24 of them into the textarea stored 16 (RDT-339).
 * Mutating the existing entry keeps every identity stable, so only the text itself re-renders.
 */
export function setBannerText(banner: Banner, language: string, text: string): void {
    const existing = banner.texts.find((entry) => entry.language === language);
    if (text.trim() === "") {
        banner.texts = banner.texts.filter((entry) => entry.language !== language);
    } else if (existing) {
        existing.text = text;
    } else {
        banner.texts.push({ language, text });
    }
}

/** Languages the banner actually has a text in, in a stable order — drives the "filled" markers. */
export function filledLanguages(banner: Pick<Banner, "texts">): string[] {
    return banner.texts
        .filter((text) => text.text.trim() !== "")
        .map((text) => text.language)
        .sort((a, b) => a.localeCompare(b));
}

/**
 * The picker works in whole days: a start covers its day from midnight and an end through its last
 * millisecond, so "1st to 31st" means what a reader expects rather than stopping at 31st 00:00.
 */
export function startFromPicker(date: Date | null): string | null {
    return date ? toServerDateOnly(date) : null;
}

export function endFromPicker(date: Date | null): string | null {
    return date ? toServerEndOfDay(date) : null;
}

/** Back to the calendar day the picker shows, in every timezone. */
export function pickerDate(value: string | null): Date | null {
    return value ? parseServerDateOnly(value) : null;
}

/**
 * A window bound as the calendar day it stands for. NOT formatDate: an end is stored at 23:59:59.999
 * UTC, which local formatting rolls into the next day east of Greenwich (a window ending 30.09 read
 * "01.10" in CEST), and a start at 00:00 UTC rolls back a day west of it.
 */
export function formatWindowDate(value: string | null): string {
    const date = pickerDate(value);
    if (!date) return "–";
    return format(date, "dd.MM.yyyy");
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Banners written before the backend started recording a person carry the keycloak subject instead.
 * A full uuid tells the reader nothing and dominates the column, so it is shortened; the cell keeps
 * the whole value in its title for the rare case someone has to look it up.
 */
export function displayAuthor(updatedBy: string | null | undefined): string {
    if (!updatedBy) return "";
    return UUID.test(updatedBy) ? `${updatedBy.slice(0, 8)}…` : updatedBy;
}

/**
 * The authored text with its markup removed, for the overview column. Rendering the markup there
 * would let one banner restyle the table; showing the raw tags just makes the column unreadable.
 *
 * A single linear pass rather than a regex: picking HTML apart with patterns is a fragile habit
 * (and one SonarQube flags as security-sensitive), while this cannot backtrack and reads plainly.
 * Runs of whitespace collapse in the same pass, so a multi-line text does not stretch the row.
 */
export function plainText(banner: Banner, ...languages: string[]): string {
    const raw = languages.map((language) => textFor(banner, language)).find((text) => text !== "") ?? "";
    let stripped = "";
    let insideTag = false;
    let lastWasSpace = false;
    for (const character of raw) {
        if (character === "<") {
            insideTag = true;
        } else if (character === ">") {
            insideTag = false;
        } else if (!insideTag) {
            const isSpace = character === " " || character === "\n" || character === "\r" || character === "\t";
            if (!isSpace) {
                stripped += character;
            } else if (!lastWasSpace && stripped !== "") {
                stripped += " ";
            }
            lastWasSpace = isSpace;
        }
    }
    return stripped.trim();
}

// ─── start-page galleria banners (RDT-359) ────────────────────────────────────

/** The two slots a start-page banner has an image for, in the order the editor lists them. */
export const BANNER_IMAGE_VARIANTS: BannerImageVariant[] = ["DESKTOP", "MOBILE"];

/**
 * What the upload accepts, as the file input's `accept` and as a client-side check. The api sniffs
 * the bytes and re-encodes everything to WebP anyway; this only spares somebody the round trip.
 */
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

/** Mirrors the api's own bound, so an oversized file is refused before it is uploaded. */
export const MAX_IMAGE_UPLOAD_BYTES = 20 * 1024 * 1024;

/**
 * What to aim for per slot, shown in the editor before a file is picked.
 *
 * The WIDTHS are the api's scaling bounds (`MAX_DESKTOP_WIDTH` / `MAX_MOBILE_WIDTH` in
 * BannerImageService.kt): anything wider is scaled down to them, so extra pixels are only a longer
 * upload, and anything narrower is never enlarged and will look soft. Keep these in step with the
 * api if those bounds ever move.
 *
 * The HEIGHTS come from the galleria band the image is fitted into with `object-fit: contain` — a
 * third of the viewport on desktop and 43% of it on a phone. They are what the shipped heroes land
 * on after processing (1920x307 and 1080x720), rounded, so a banner built to these fills the band
 * instead of being letterboxed.
 */
export const RECOMMENDED_IMAGE_DIMENSIONS: Record<BannerImageVariant, { width: number; height: number }> = {
    DESKTOP: { width: 1920, height: 320 },
    MOBILE: { width: 1080, height: 720 },
};

/**
 * A blank start-page banner for the create dialog: off, open-ended, everyone, no image picked yet.
 * `position` is the caller's business — it is the end of the current list.
 */
export function emptyStartPageBanner(position = 0): StartPageBanner {
    return {
        id: null,
        active: false,
        startsAt: null,
        endsAt: null,
        audience: "ALL",
        position,
        link: null,
        desktopImageHash: "",
        mobileImageHash: null,
        texts: [],
    };
}

/**
 * The same rules language-management enforces, checked here so the editor can say what is wrong
 * before saving rather than surfacing a 422. Returns an i18n key, or null when the banner is fine.
 */
export function startPageBannerProblem(banner: StartPageBanner): string | null {
    if (!banner.desktopImageHash) return "startPageBanners.error.noDesktopImage";
    if (!startPageBannerAlt(banner, REQUIRED_LANGUAGE)) return "startPageBanners.error.noEnglishAlt";
    if (banner.startsAt && banner.endsAt && parseServerDate(banner.endsAt) < parseServerDate(banner.startsAt)) {
        return "banners.error.endBeforeStart";
    }
    if (!isUsableBannerLink(banner.link)) return "startPageBanners.error.badLink";
    return null;
}

/**
 * Schemes that would make the start page a script sink, since the galleria renders the link as an
 * href. Denied by name rather than left to the `://` rule below, because `javascript://x%0Aalert(1)`
 * DOES carry an authority — the `//` opens a JS comment and the newline runs the rest.
 */
const DENIED_LINK_SCHEMES = ["javascript:", "data:", "vbscript:", "blob:", "file:"];

/** Schemes that address something other than a host, so they legitimately have no `//`. */
const SCHEME_ONLY_LINKS = ["mailto:", "tel:"];

/**
 * Any `scheme://…` link is accepted, not just http and https — a banner may point at whatever
 * marketing needs — with the two opaque schemes above allowed explicitly and the dangerous ones
 * denied outright. A bare host stays invalid: without a scheme the browser would resolve it
 * relative to the start page.
 */
export function isUsableBannerLink(link: string | null | undefined): boolean {
    const trimmed = link?.trim();
    if (!trimmed) return true;
    try {
        const { protocol } = new URL(trimmed);
        if (DENIED_LINK_SCHEMES.includes(protocol)) return false;
        if (SCHEME_ONLY_LINKS.includes(protocol)) return true;
        return trimmed.toLowerCase().startsWith(`${protocol}//`);
    } catch {
        return false;
    }
}

/** The banner's alt text in one language, or "" when it has none — blank texts do not count. */
export function startPageBannerAlt(banner: StartPageBanner, language: string): string {
    return banner.texts.find((text) => text.language === language && text.text.trim() !== "")?.text ?? "";
}

/**
 * Sets one language's alt text, dropping the entry when it is blanked. Mutates for the same reason
 * [setBannerText] does: the editor calls this on every keystroke, and replacing the object re-rendered
 * the dialog and lost characters out of fast typing (RDT-339).
 */
export function setStartPageBannerAlt(banner: StartPageBanner, language: string, text: string): void {
    const existing = banner.texts.find((entry) => entry.language === language);
    if (text.trim() === "") {
        banner.texts = banner.texts.filter((entry) => entry.language !== language);
    } else if (existing) {
        existing.text = text;
    } else {
        banner.texts.push({ language, text });
    }
}

/** Rounded to one decimal, because "123.4 KB" is what a person checking a file size wants to read. */
export function formatImageSize(bytes: number | null | undefined): string {
    if (!bytes) return "–";
    return `${(bytes / 1024).toFixed(1)} KB`;
}

/** "1920 × 307", with the multiplication sign rather than a letter x. */
export function formatImageDimensions(image: { width: number; height: number } | null | undefined): string {
    return image ? `${image.width} × ${image.height}` : "–";
}

/**
 * Why an upload cannot be accepted, as an i18n key, or null when it is fine. Only the two checks the
 * api would make anyway — the point is to fail before sending 20 MB across the wire, not to duplicate
 * the api's validation, which sniffs the actual bytes.
 */
export function imageUploadProblem(file: File): string | null {
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) return "startPageBanners.error.imageTooLarge";
    if (file.type && !ACCEPTED_IMAGE_TYPES.includes(file.type)) return "startPageBanners.error.imageWrongType";
    return null;
}

/**
 * Moves a banner one place through the carousel and renumbers the whole list, so `position` stays a
 * dense 0..n-1 sequence rather than drifting into gaps that make the next move ambiguous. Returns the
 * banners whose position actually changed — the only ones worth a PUT.
 */
export function reorderStartPageBanners(
    banners: StartPageBanner[],
    id: number,
    direction: -1 | 1,
): StartPageBanner[] {
    const ordered = [...banners].sort((a, b) => a.position - b.position || (a.id ?? 0) - (b.id ?? 0));
    const from = ordered.findIndex((banner) => banner.id === id);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= ordered.length) return [];
    [ordered[from], ordered[to]] = [ordered[to], ordered[from]];
    return ordered
        .map((banner, index) => ({ ...banner, position: index }))
        .filter((banner, index) => banners.find((original) => original.id === banner.id)?.position !== index);
}
